import React, { useState } from "react";
import moment from "moment";
import { ExecutionResult } from "graphql";
import { MutationFunctionOptions } from "react-apollo";
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Theme,
    makeStyles
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/DeleteOutlined";
import { useSnackbar } from "notistack";
import PerfectScrollbar from "react-perfect-scrollbar";
import { OPERATOR, WhereConstraints } from "../../../graphql/common.datatypes";
import { Note } from "../../../graphql/note.datatypes";
import { formatDateTime } from "../../../utils/formatters";
import getNotes from "../api/getNotes.api";
import deleteNote from "../api/deleteNote.api";
import { LoadingOverlay } from "../../../components/LoadingOverlay";
import { UpdateNoteModal } from "./UpdateNoteModal";
import { Can } from "../../../components/Can";
import { ConfirmDelete } from "../../../components/ConfirmDelete";

interface Props {
    type_id: number;
    type: "user" | "group" | "room_party" | "billing_party" | "group_member";
}

interface FilterValues {
    note: string;
    notes: string[];
    startDate: Date | null;
    endDate: Date | null;
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: "relative"
    },
    content: {
        padding: 0
    },
    inner: {
        [theme.breakpoints.up("md")]: {
            minWidth: 700
        }
    },
    nameCell: {
        display: "flex",
        alignItems: "center"
    },
    avatar: {
        height: 42,
        width: 42,
        marginRight: theme.spacing(1)
    },
    actions: {
        padding: theme.spacing(1),
        justifyContent: "flex-end"
    }
}));

export const NotesList = (props: Props) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [page, setPage] = useState(1);
    const [selectedNote, setSelectedNote] = useState<Note | undefined>(
        undefined
    );
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const [noteToDelete, setNoteToDelete] = useState<Note | undefined>(
        undefined
    );

    const initialFilters: FilterValues = {
        note: "",
        notes: [],
        startDate: moment("2019-01-01T00:00").toDate(),
        endDate: moment()
            .endOf("day")
            .toDate()
    };

    const applyFilters = (newFilters: FilterValues) => {
        setFilters(newFilters);
        setWhere(getConstraintsFromFilters(newFilters));
    };

    const getConstraintsFromFilters = (newFilters: FilterValues) => {
        const AND: WhereConstraints[] = [];
        const columns = {
            user: "user_id",
            group: "group_id",
            room_party: "room_party_id",
            billing_party: "billing_party_id",
            group_member: "group_member_id"
        };

        const column = columns[props.type];

        AND.push({
            column,
            operator: OPERATOR.EQ,
            value: props.type_id
        });

        if (newFilters.startDate && newFilters.endDate) {
            AND.push({
                column: "created_at",
                operator: OPERATOR.BETWEEN,
                value: [newFilters.startDate, newFilters.endDate]
            });
        } else {
            if (newFilters.startDate) {
                AND.push({
                    column: "created_at",
                    operator: OPERATOR.GTE,
                    value: newFilters.startDate
                });
            } else if (newFilters.endDate) {
                AND.push({
                    column: "created_at",
                    operator: OPERATOR.LTE,
                    value: newFilters.endDate
                });
            }
        }

        return {
            AND
        };
    };

    const [filters, setFilters] = useState(initialFilters);
    const [where, setWhere] = useState(
        getConstraintsFromFilters(initialFilters)
    );
    const [orderBy] = useState([{ column: "created_at", order: "DESC" }]);
    const notesData = getNotes(rowsPerPage, page, where, orderBy);
    const [sendDeleteNote, deleteNoteData] = deleteNote();

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        page: number
    ) => {
        setPage(page + 1);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
        setRowsPerPage(Number(event.target.value));
    };

    const handleDeleteNote = () => {
        if (!noteToDelete) {
            return;
        }

        sendDeleteNote({
            variables: {
                id: noteToDelete.id
            }
        })
            .then(() => {
                handleConfirmationClose();
                enqueueSnackbar("Note deleted successfully", {
                    variant: "success"
                });
            })
            .catch(() => {
                enqueueSnackbar("Error deleting note", {
                    variant: "error"
                });
            });
    };

    const onNoteDeleted = (note: Note) => {
        setNoteToDelete(note);
        setOpenDeleteConfirmation(true);
    };

    const handleConfirmationClose = () => {
        setNoteToDelete(undefined);
        setOpenDeleteConfirmation(false);
    };

    return (
        <React.Fragment>
            <LoadingOverlay loading={notesData.loading}>
                {notesData.data && notesData.data.notes && (
                    <div>
                        <Card>
                            <CardHeader title={"Notes"} />
                            <Divider />
                            <CardContent className={classes.content}>
                                <PerfectScrollbar>
                                    <div className={classes.inner}>
                                        <TableData
                                            data={notesData.data.notes.data}
                                            selectedNote={selectedNote}
                                            setSelectedNote={setSelectedNote}
                                            setDeleteNote={onNoteDeleted}
                                        />
                                    </div>
                                </PerfectScrollbar>
                            </CardContent>
                            <CardActions className={classes.actions}>
                                <TablePagination
                                    component="div"
                                    count={
                                        notesData.data.notes.paginatorInfo.total
                                    }
                                    onChangePage={handleChangePage}
                                    onChangeRowsPerPage={
                                        handleChangeRowsPerPage
                                    }
                                    page={
                                        notesData.data.notes.paginatorInfo
                                            .currentPage - 1
                                    }
                                    rowsPerPage={rowsPerPage}
                                    rowsPerPageOptions={[25, 50, 100]}
                                />
                            </CardActions>
                        </Card>
                    </div>
                )}
            </LoadingOverlay>
            {selectedNote && (
                <UpdateNoteModal
                    note={selectedNote}
                    onClose={refetch => {
                        if (refetch) {
                            notesData.refetch();
                        }
                        setSelectedNote(undefined);
                    }}
                />
            )}

            <ConfirmDelete
                onClose={handleConfirmationClose}
                onDeleteClick={handleDeleteNote}
                open={openDeleteConfirmation}
            />
        </React.Fragment>
    );
};

const TableData = (props: {
    data: Note[];
    selectedNote: Note | undefined;
    setSelectedNote: (note: Note) => void;
    setDeleteNote: (note: Note) => void;
}) => {
    const { data, selectedNote, setSelectedNote, setDeleteNote } = props;
    // const timezone = moment.tz.guess();

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell style={{ width: 215 }}>Created At</TableCell>
                    <TableCell style={{ width: 215 }}>Updated At</TableCell>
                    <TableCell style={{ width: 215 }}>Created By</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {data.map(note => {
                    return (
                        <TableRow
                            hover
                            key={note.id}
                            selected={selectedNote === note}
                            onClick={() => setSelectedNote(note)}
                        >
                            <TableCell>
                                {formatDateTime(note.created_at)}
                            </TableCell>
                            <TableCell>
                                {formatDateTime(note.updated_at)}
                            </TableCell>
                            <TableCell>
                                {note && note.log && note.log.user ? (
                                    <span>
                                        {note.log.user.first_name}{" "}
                                        {note.log.user.last_name}
                                    </span>
                                ) : (
                                    <span>System</span>
                                )}
                            </TableCell>
                            <TableCell>{note.note}</TableCell>
                            <TableCell>
                                <IconButton
                                    aria-label="delete"
                                    onClick={e => {
                                        e.stopPropagation();
                                        setDeleteNote(note);
                                    }}
                                >
                                    <Can permission="notes.delete">
                                        <DeleteIcon />
                                    </Can>
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
};
